// src/oura/overview/WhatNextCard.tsx
//
// "What to do next": the top of Fixes, split by who moves next. Two sections,
// a firm rule under each heading, and one status per row, so the card answers
// "whose turn is it" without the reader opening anything (D11).
//
// Each heading's note names what the section holds (due now, questions and
// tasks), because Fixes and Client count the same labels over the whole plan.
// "Needs us" also carries what the client started and we owe (a change until
// it is Done, a call until it is scheduled), plan or no plan.

import type { ReactNode } from "react";
import { ClipboardListIcon } from "lucide-react";
import type { BusinessTracker } from "../tracker";
import type { Load } from "../hooks/useOverviewData";
import { dayDate } from "../lib/oura";
import { ArrowLink, Dot, FamilyChip, FoldCard, LATE_TEXT, ListRow, SectionHead, ShowMore, Tag, TYPE, useMediaQuery, type Tone } from "@oura/ui";
import { familyOf } from "../components/plan/planModel";
import { cn } from "../lib/cn";
import { count, couldNotRead, emptyColumnNote, isLate, NEXT_ROWS_SHOWN, staffHref, type NextRow, type NextStatus, type NextWork } from "./model";
/** Below the 640px breakpoint: one column, and no room for a status beside a title. */
const PHONE = "(max-width: 639px)";

function statusText(status: NextStatus): string {
    switch (status.kind) {
        case "late":
            return `${count(status.days, "day")} late`;
        case "came_back":
            return "Came back";
        case "client_turn":
            // No claim that nobody asked: the client is asked in the Google
            // Docs now, which Oura cannot match to a task (YW, 29 Sep 2026).
            return "Waiting on client";
        case "request":
            return status.answered ? "Answered, not done" : "Needs a reply";
        case "in_progress": {
            const due = dayDate(status.due);
            return due ? `Due ${due}` : "In progress";
        }
        case "waiting":
            return "Waiting on a reply";
        case "blocked":
            return "Blocked";
        case "not_started":
            return "Not started";
        case "unplanned":
            return status.outside ? "Not in the contract" : "Not in the plan";
    }
}

/** Amber is the client's move, blue ours, grey not begun (D8). Blocked keeps the Tracker's rose. */
function dotTone(status: NextStatus, clientSide: boolean): Tone {
    switch (status.kind) {
        case "waiting":
        case "client_turn":
            return "amber";
        case "late":
        case "in_progress":
            return clientSide ? "amber" : "blue";
        case "request":
            return "blue";
        case "blocked":
        case "came_back":
            return "rose";
        case "not_started":
        case "unplanned":
            return "gray";
    }
}

/**
 * The status in its colour. Only "Came back" is rose on a row: when 9 rows of
 * 12 were red nothing stood out (audit, 24 Oct 2026), and the group's own
 * "N late" count above them already says it in red. A late row is dark grey. In progress and Not
 * started each carry a word of their own, then the due date: a bare "Due Wed 28
 * Oct" beside a muted "Not started" read alike (#4737).
 */
function statusNode(status: NextStatus): ReactNode {
    if (status.kind === "came_back") return <span className={LATE_TEXT}>{statusText(status)}</span>;
    if (status.kind === "late") return <span className="font-medium text-gray-700">{statusText(status)}</span>;
    if (status.kind === "in_progress" || status.kind === "not_started") {
        const due = dayDate(status.due ?? null);
        return (
            <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-1">
                <Tag tone={status.kind === "in_progress" ? "blue" : "gray"}>{status.kind === "in_progress" ? "In progress" : "Not started"}</Tag>
                {due && <span>Due {due}</span>}
            </span>
        );
    }
    return statusText(status);
}

function Rows({ rows, clientSide, businessId }: { rows: NextRow[]; clientSide: boolean; businessId: string }) {
    // On a phone the status moves under the title: beside it, a truncated
    // title and "Waiting on a reply" left two words of the work itself.
    const phone = useMediaQuery(PHONE);
    const row = (r: NextRow) => {
        const status = statusNode(r.status);
        return (
            <ListRow
                key={r.key}
                className="min-h-[58px]"
                icon={<Dot tone={dotTone(r.status, clientSide)} />}
                title={phone ? <span className="whitespace-normal">{r.title}</span> : r.title}
                sub={phone ? status : undefined}
                muted={r.status.kind === "not_started"}
                meta={phone ? undefined : status}
                tags={r.code ? <FamilyChip family={familyOf(r.code)}>{r.code}</FamilyChip> : undefined}
                to={staffHref(businessId, r.link)}
            />
        );
    };
    return (
        <>
            {/* The rest of the plan is one click away on Fixes. */}
            <div>{rows.slice(0, NEXT_ROWS_SHOWN).map(row)}</div>
            <ShowMore count={rows.length - NEXT_ROWS_SHOWN}>{rows.slice(NEXT_ROWS_SHOWN).map(row)}</ShowMore>
        </>
    );
}

function Note({ children }: { children: string }) {
    return <p className={cn(TYPE.tc, "m-0 py-3 text-gray-500")}>{children}</p>;
}

function Placeholder() {
    return (
        <div aria-hidden>
            {[0, 1].map((i) => (
                <div key={i} className="flex min-h-[58px] items-center gap-3 border-t border-gray-100 first:border-t-0">
                    <span className="h-2 w-2 rounded-full bg-gray-200" />
                    <span className="h-3 w-1/2 animate-pulse rounded bg-gray-100" />
                </div>
            ))}
        </div>
    );
}

/** "3 due now · 1 late": the rows this section lists, which is not the plan's whole count. */
function usSummary(rows: NextRow[]): ReactNode {
    if (!rows.length) return undefined;
    const late = rows.filter(isLate).length;
    return (
        <>
            {rows.length} due now
            {late > 0 && (
                <>
                    {" · "}
                    <span className={LATE_TEXT}>{late} late</span>
                </>
            )}
        </>
    );
}

/** "2 late · 4 questions, 1 task": what the client holds up, our questions to them apart from their own tasks. */
function clientSummary(rows: NextRow[]): ReactNode {
    if (!rows.length) return undefined;
    const late = rows.filter(isLate).length;
    const questions = rows.filter((r) => r.asked).length;
    const tasks = rows.length - questions;
    const mix = [questions > 0 && count(questions, "question"), tasks > 0 && count(tasks, "task")].filter(Boolean).join(", ");
    if (!late) return mix;
    return (
        <>
            <span className={LATE_TEXT}>{late} late</span>
            {` · ${mix}`}
        </>
    );
}

export function WhatNextCard({
    businessId,
    plan,
    asks,
    next,
    recommendations,
    delivery = null,
    onboarding = "off",
    nextCardCovers = false,
}: {
    businessId: string;
    plan: Load<BusinessTracker | null>;
    /** Where our questions to the client stand: the client's page carries them. */
    asks: Load<unknown>["status"];
    next: NextWork;
    /** Shown under Needs us when there is no plan to read. */
    recommendations: NextRow[];
    /**
     * Delivery's load when Delivery is on (null: Fixes is the plan). Ready: its
     * deliverables are the plan. None: no contract in Oura, and Fixes is never
     * read in its place. The head opens Delivery either way.
     */
    delivery?: "loading" | "failed" | "none" | "ready" | null;
    /**
     * Where the onboarding read stands. Onboarding steps feed BOTH columns, so
     * a read that did not land has to be said rather than shown as nothing: a
     * failed read and a client with no open steps are different facts, and
     * An empty column once read "Nothing waits on the client", which is the claim
     * this card was fixed for
     * (Jack, #4011). `off` is "not asked" -- a past scan, or the gate shut --
     * which is not a gap in what the card knows.
     */
    onboarding?: Load<unknown>["status"];
    /**
     * The stage's Next card above already says what comes before a plan (a
     * prospect, a client being set up): the "no contract" line is not said
     * beside it.
     */
    nextCardCovers?: boolean;
}) {
    const deliveryOn = delivery != null;
    const hasPlan = deliveryOn ? delivery === "ready" : plan.status === "ready" && plan.value !== null;
    const loading = deliveryOn ? delivery === "loading" : plan.status === "loading";
    const fixes = staffHref(businessId, { view: deliveryOn ? "delivery" : "todo" });
    // A section built from a source that did not answer makes no count, and
    // never says "nothing asked": it does not know.
    const unread = couldNotRead([
        ...(deliveryOn ? (delivery === "failed" ? ["Delivery"] : []) : plan.status === "failed" ? ["the plan"] : []),
        ...(asks === "failed" ? ["the client's page"] : []),
        ...(onboarding === "failed" ? ["the onboarding steps"] : []),
    ]);
    // Onboarding is the one source that feeds both columns, so it holds back
    // both empty notes, not just the client's.
    const onboardingUnread = onboarding === "failed" ? couldNotRead(["the onboarding steps"]) : null;
    const clientLoading = asks === "loading" || loading || onboarding === "loading";
    const noPlanNote = deliveryOn
        ? delivery === "failed"
            ? "Delivery could not be read."
            : "No contract in Oura yet, so no Delivery. Add it under Delivery, Plan."
        : plan.status === "failed"
          ? "The plan could not be read."
          : "No plan yet. Build it on Fixes.";

    return (
        <FoldCard
            id="what-next"
            title="What to do next"
            icon={{ icon: ClipboardListIcon, area: "work" }}
            right={<ArrowLink to={fixes}>{hasPlan ? `All ${next.open} open` : deliveryOn ? "Open Delivery" : "Open Fixes"}</ArrowLink>}
        >
            <SectionHead first title="Needs us" summary={hasPlan ? usSummary(next.us) ?? "0 pending" : undefined} />
            {loading || (onboarding === "loading" && next.us.length === 0) ? (
                <Placeholder />
            ) : hasPlan ? (
                next.us.length ? (
                    <>
                        <Rows rows={next.us} clientSide={false} businessId={businessId} />
                        {/* Rows do not mean the list is whole. A staff-owned
                            onboarding step could be missing from it, so the
                            gap is said under the rows, in the same words the
                            client column uses (Jack, #4011). */}
                        {onboardingUnread && <Note>{`${onboardingUnread}, so this list may be short.`}</Note>}
                    </>
                ) : (
                    <Note>{emptyColumnNote("0 pending.", onboardingUnread)}</Note>
                )
            ) : (
                <>
                    {/* With no plan, Needs us holds only what the client started. */}
                    {next.us.length > 0 && <Rows rows={next.us} clientSide={false} businessId={businessId} />}
                    {nextCardCovers && delivery !== "failed" && plan.status !== "failed" ? (
                        // "Nothing yet." only when no row above says otherwise (Jack, #4285).
                        recommendations.length > 0 ? (
                            <Note>These are the scan&apos;s top recommendations.</Note>
                        ) : next.us.length === 0 ? (
                            <Note>Nothing yet.</Note>
                        ) : null
                    ) : (
                        <Note>{recommendations.length > 0 ? `${noPlanNote} These are the scan's top recommendations.` : noPlanNote}</Note>
                    )}
                    {recommendations.length > 0 && <Rows rows={recommendations} clientSide={false} businessId={businessId} />}
                </>
            )}

            <SectionHead title="Waiting on client" summary={unread || clientLoading ? undefined : clientSummary(next.client) ?? "0 pending"} />

            {next.client.length > 0 && <Rows rows={next.client} clientSide businessId={businessId} />}
            {clientLoading ? (
                next.client.length === 0 && <Placeholder />
            ) : unread ? (
                <Note>{next.client.length ? `${unread}, so this list may be short.` : `${unread}.`}</Note>
            ) : (
                // "0 pending", not "Nothing waits on the client" (#4026
                // C-FE-6): a number a reader can compare with yesterday's, and
                // the same word the client's own screens use. "Nothing asked
                // yet" stays -- no plan is a different state from an empty
                // one, and saying "0 pending" about a business nobody has
                // asked anything would read as up to date.
                next.client.length === 0 && <Note>{hasPlan ? "0 pending." : "Nothing asked yet."}</Note>
            )}
        </FoldCard>
    );
}
